import { z } from 'zod';

import { StationSchema } from './station.schema.js';
import {
  stationDeviceClaimStatusValues,
  type CreateStationRecoveryClaimRequest,
  type CreateStationRecoveryClaimResponse,
  type FailStationRecoveryClaimRequest,
  type FailStationRecoveryClaimResponse,
  type ListRecoverableStationsRequest,
  type ListRecoverableStationsResponse,
  type RecoverableStationDto,
  type StationDeviceClaimDto,
  type StationDeviceClaimSummaryDto,
  type StationDeviceClaimStatus,
  type StationRecoveryGrantDto,
} from './station-recovery.dto.js';

const timestampSchema = z.iso.datetime();

export const StationDeviceClaimStatusSchema: z.ZodType<StationDeviceClaimStatus> = z.enum(
  stationDeviceClaimStatusValues,
);

const stationDeviceClaimSummaryObject = z.object({
  id: z.uuid(),
  deviceId: z.uuid(),
  status: StationDeviceClaimStatusSchema,
  authorizedAt: timestampSchema,
  activatedAt: timestampSchema.nullable(),
  supersededAt: timestampSchema.nullable(),
  failedAt: timestampSchema.nullable(),
});

export const StationDeviceClaimSummarySchema: z.ZodType<StationDeviceClaimSummaryDto> =
  stationDeviceClaimSummaryObject;

export const StationDeviceClaimSchema: z.ZodType<StationDeviceClaimDto> =
  stationDeviceClaimSummaryObject.extend({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    previousDeviceClaimId: z.uuid().nullable(),
    supersededByClaimId: z.uuid().nullable(),
    recoveryReason: z.string().trim().min(1),
    authorizedByUserId: z.uuid().nullable(),
    authorizedByMembershipId: z.uuid().nullable(),
    failureReason: z.string().trim().min(1).nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const RecoverableStationSchema: z.ZodType<RecoverableStationDto> = z.object({
  station: StationSchema,
  activeDeviceClaim: StationDeviceClaimSummarySchema.nullable(),
});

export const ListRecoverableStationsRequestSchema: z.ZodType<ListRecoverableStationsRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    page: z.number().int().min(1).optional(),
    pageSize: z.number().int().min(1).max(200).optional(),
  });

export const ListRecoverableStationsResponseSchema: z.ZodType<ListRecoverableStationsResponse> =
  z.object({
    items: z.array(RecoverableStationSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(200),
    total: z.number().int().min(0),
  });

export const CreateStationRecoveryClaimRequestSchema: z.ZodType<CreateStationRecoveryClaimRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    deviceId: z.uuid(),
    reason: z.string().trim().min(1),
    desktopReleaseChannel: z.string().trim().min(1).max(64).optional(),
    desktopAppVersion: z.string().trim().min(1).max(64).optional(),
  });

export const StationRecoveryGrantSchema: z.ZodType<StationRecoveryGrantDto> = z.object({
  claim: StationDeviceClaimSchema,
  station: StationSchema,
  issuedAt: timestampSchema,
  expiresAt: timestampSchema,
});

export const CreateStationRecoveryClaimResponseSchema: z.ZodType<CreateStationRecoveryClaimResponse> =
  z.object({
    recoveryGrant: StationRecoveryGrantSchema,
  });

export const FailStationRecoveryClaimRequestSchema: z.ZodType<FailStationRecoveryClaimRequest> =
  z.object({
    companyId: z.uuid(),
    claimId: z.uuid(),
    deviceId: z.uuid(),
    reason: z.string().trim().min(1),
  });

export const FailStationRecoveryClaimResponseSchema: z.ZodType<FailStationRecoveryClaimResponse> =
  z.object({
    claim: StationDeviceClaimSchema,
  });
